// File: src/components/Maintenance/MaintenanceSummary.jsx
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  PauseCircle,
  ShieldAlert,
  Wrench,
} from "lucide-react";

const INITIAL = {
  openTasks: 0,
  inProgressTasks: 0,
  onHoldTasks: 0,
  closedTasks: 0,
  criticalOpenTasks: 0,
};

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const pick = (obj, keys, fallback = null) => {
  for (const key of keys) {
    if (
      obj &&
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] !== undefined
    ) {
      return obj[key];
    }
  }
  return fallback;
};

const normalizeSummary = (raw) => {
  const root = isObj(raw) ? raw : {};
  const summary = isObj(root.summary) ? root.summary : {};
  const totals = isObj(root.totals) ? root.totals : {};
  const metrics = isObj(root.metrics) ? root.metrics : {};
  const kpis = isObj(root.kpis) ? root.kpis : {};

  const read = (keys) =>
    toNum(
      pick(root, keys, null) ??
        pick(summary, keys, null) ??
        pick(totals, keys, null) ??
        pick(metrics, keys, null) ??
        pick(kpis, keys, 0)
    );

  return {
    openTasks: read(["openTasks", "open_tasks", "open"]),
    inProgressTasks: read([
      "inProgressTasks",
      "in_progress_tasks",
      "inProgress",
      "in_progress",
    ]),
    onHoldTasks: read(["onHoldTasks", "on_hold_tasks", "onHold", "on_hold"]),
    closedTasks: read(["closedTasks", "closed_tasks", "closed"]),
    criticalOpenTasks: read([
      "criticalOpenTasks",
      "critical_open_tasks",
      "critical",
      "critical_open",
    ]),
  };
};

const formatCount = (value) =>
  new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
  }).format(toNum(value, 0));

function StatTile({ label, value, helper, icon, tone = "slate", strong = false }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };

  return (
    <div className={`rounded-3xl border p-5 ${tones[tone] || tones.slate}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-700">{label}</div>
          <div
            className={`mt-3 leading-none text-slate-950 ${
              strong ? "text-5xl font-black" : "text-3xl font-black"
            }`}
          >
            {formatCount(value)}
          </div>
          {helper ? <div className="mt-3 text-xs leading-5 text-slate-600">{helper}</div> : null}
        </div>

        <div className="shrink-0 rounded-2xl bg-white/80 p-3 shadow-sm">{icon}</div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, icon, tone = "slate" }) {
  const tones = {
    slate: "text-slate-700",
    sky: "text-sky-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    violet: "text-violet-700",
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <div className={`rounded-xl bg-slate-100 p-2 ${tones[tone] || tones.slate}`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>

      <span className="shrink-0 text-lg font-bold text-slate-950">{formatCount(value)}</span>
    </div>
  );
}

export default function MaintenanceSummary({
  onClick,
  data = null,
  loading = false,
  error = "",
  source = null,
  refreshKey = 0,
}) {
  const [summary, setSummary] = useState(INITIAL);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState("");

  const hasParentData = useMemo(() => isObj(data), [data]);

  useEffect(() => {
    let alive = true;

    if (hasParentData) {
      setSummary(normalizeSummary(data));
      setLocalError("");
      setLocalLoading(false);

      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        setLocalLoading(true);
        setLocalError("");

        const res = await api.get("/solo-mdsh-api/maintenance/summary");
        if (!alive) return;

        setSummary(normalizeSummary(res?.data));
      } catch (e) {
        console.error("Failed to fetch maintenance summary", e);
        if (!alive) return;

        setSummary(INITIAL);
        setLocalError(
          e?.response?.data?.error ||
            e?.message ||
            "Failed to load maintenance summary"
        );
      } finally {
        if (alive) setLocalLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [hasParentData, data, refreshKey]);

  const effectiveLoading = loading && !hasParentData ? true : localLoading;
  const effectiveError = error || localError;
  const clickable = typeof onClick === "function";

  const totalActive = useMemo(
    () => summary.openTasks + summary.inProgressTasks + summary.onHoldTasks,
    [summary]
  );

  const completionRate = useMemo(() => {
    const total = totalActive + summary.closedTasks;
    if (!total) return 0;
    return Math.round((summary.closedTasks / total) * 100);
  }, [summary.closedTasks, totalActive]);

  const status = useMemo(() => {
    if (effectiveLoading) {
      return {
        label: "Loading",
        headline: "Loading maintenance control data...",
        body: "Maintenance status is being refreshed from the live system.",
        banner: "border-slate-200 bg-slate-50 text-slate-800",
        badge: "border-slate-200 bg-white text-slate-700",
      };
    }

    if (effectiveError) {
      return {
        label: "Review",
        headline: "Maintenance summary could not be fully loaded.",
        body: "The card is protected with safe defaults, but the endpoint should be checked.",
        banner: "border-amber-200 bg-amber-50 text-amber-900",
        badge: "border-amber-200 bg-white text-amber-800",
      };
    }

    if (summary.criticalOpenTasks > 0) {
      return {
        label: "Urgent",
        headline: "Critical maintenance issues need immediate attention.",
        body: "Prioritise urgent open tasks before routine maintenance work.",
        banner: "border-rose-200 bg-rose-50 text-rose-900",
        badge: "border-rose-200 bg-white text-rose-800",
      };
    }

    if (summary.openTasks > 0 || summary.inProgressTasks > 0) {
      return {
        label: "Active",
        headline: "Maintenance work is currently active.",
        body: "Open and in-progress tasks should be monitored for delay or escalation.",
        banner: "border-sky-200 bg-sky-50 text-sky-900",
        badge: "border-sky-200 bg-white text-sky-800",
      };
    }

    if (summary.closedTasks > 0) {
      return {
        label: "Stable",
        headline: "Recent maintenance work has been completed.",
        body: "No urgent active maintenance pressure is visible from the summary.",
        banner: "border-emerald-200 bg-emerald-50 text-emerald-900",
        badge: "border-emerald-200 bg-white text-emerald-800",
      };
    }

    return {
      label: "Quiet",
      headline: "No major maintenance activity detected right now.",
      body: "The maintenance workload appears quiet based on the current summary.",
      banner: "border-slate-200 bg-slate-50 text-slate-800",
      badge: "border-slate-200 bg-white text-slate-700",
    };
  }, [effectiveLoading, effectiveError, summary]);

  if (effectiveLoading) {
    return (
      <section className="h-full rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm animate-pulse">
        <div className="h-7 w-52 rounded bg-slate-200" />
        <div className="mt-5 h-24 rounded-3xl bg-slate-100" />
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="h-36 rounded-3xl bg-slate-100" />
          <div className="h-36 rounded-3xl bg-slate-100" />
        </div>
        <div className="mt-5 h-64 rounded-3xl bg-slate-100" />
      </section>
    );
  }

  return (
    <section
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`h-full rounded-[1.75rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-rose-50 p-6 shadow-sm transition ${
        clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg" : ""
      }`}
    >
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">
            Maintenance Command
          </div>

          <h3 className="mt-3 text-3xl font-black tracking-tight text-slate-950">
            Maintenance
          </h3>

          <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
            Open work, critical tasks, hold status, completion progress, and current operational
            pressure.
          </p>
        </div>

        <div className="flex flex-col items-start gap-2 xl:items-end">
          <div
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold ${status.badge}`}
          >
            {status.label}
          </div>

          {clickable ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-sky-700">
              Open dashboard
              <ArrowRight size={14} />
            </div>
          ) : null}

          {source ? <div className="text-[11px] text-slate-500">Source: {source}</div> : null}
        </div>
      </div>

      {effectiveError ? (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800">
          {effectiveError}
        </div>
      ) : null}

      <div className={`mt-5 rounded-3xl border px-5 py-4 ${status.banner}`}>
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-black">{status.headline}</div>
            <p className="mt-1 text-sm leading-6 opacity-90">{status.body}</p>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatTile
          label="Active Workload"
          value={totalActive}
          helper="Open, in-progress, and on-hold tasks combined."
          icon={<Wrench size={22} />}
          tone={totalActive > 0 ? "amber" : "slate"}
          strong
        />

        <StatTile
          label="Critical Open"
          value={summary.criticalOpenTasks}
          helper="Tasks that need urgent management review."
          icon={<AlertCircle size={22} />}
          tone={summary.criticalOpenTasks > 0 ? "rose" : "emerald"}
          strong
        />
      </div>

      <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
              Task Movement
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Current workload split by task stage.
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-right">
            <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
              Completion
            </div>
            <div className="text-lg font-black text-slate-950">{completionRate}%</div>
          </div>
        </div>

        <div className="mt-4">
          <DetailRow
            label="Open"
            value={summary.openTasks}
            icon={<Wrench size={18} />}
            tone="amber"
          />

          <DetailRow
            label="In Progress"
            value={summary.inProgressTasks}
            icon={<Clock size={18} />}
            tone="sky"
          />

          <DetailRow
            label="On Hold"
            value={summary.onHoldTasks}
            icon={<PauseCircle size={18} />}
            tone="violet"
          />

          <DetailRow
            label="Closed"
            value={summary.closedTasks}
            icon={<CheckCircle size={18} />}
            tone="emerald"
          />
        </div>
      </div>

      <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
            <Wrench size={20} />
          </div>

          <div>
            <div className="text-sm font-black text-slate-950">Maintenance Health</div>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              This card is wired for the long-term maintenance workflow: task management, asset
              history, inspection reminders, PO linkage, and future cost control.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}